import { DataTable } from "@/components/datatable/components/DataTable";
import { tableSearchSchema, safeDecompress } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import PageHeader from "@/components/common/PageHeader";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import useConfirm from "@/hooks/useConfirm";
import { chargesKeys, statsKeys } from "@/query-keys";
import { type ChargeListItem } from "@/schemas/chargeSchema";
import { services } from "@/services";
import useChargesQuery from "./_hooks/useChargesQuery";
import { createFileRoute } from "@tanstack/react-router";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import CreateChargeForm from "./_components/CreateChargeForm";
import ChargesKpis from "./_components/ChargesKpis";
import useChargesColumns from "./_hooks/useColumns";
import useChargeMutation from "./_hooks/useChargeMutation";
import useChargeExport from "./_hooks/useChargeExport";

export const Route = createFileRoute("/_private/charges/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    const [charges] = await Promise.all([
      queryClient.ensureQueryData({
        queryKey: chargesKeys.lists(deps.q),
        queryFn: () => services.getCharges(requestParams),
      }),
      queryClient.ensureQueryData({
        queryKey: statsKeys.charges(),
        queryFn: () => services.getChargeStats(),
      }),
    ]);
    return charges;
  },
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorBoundary error={error} />,
});

function RouteComponent() {
  const search = Route.useSearch();
  const { t } = useTranslation();
  const loaderData = Route.useLoaderData();
  const { data: queryData } = useChargesQuery(search.q, loaderData);

  const columns = useChargesColumns();
  const { deleteCharge, isDeleting } = useChargeMutation();
  const { exportCharges, isExporting } = useChargeExport();

  const deleteConfirm = useConfirm({
    title: t("charges.confirm.delete.title"),
    description: t("charges.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleDelete = useCallback(
    (row: ChargeListItem) => {
      deleteConfirm.confirm(() => {
        deleteCharge(row.id);
      });
    },
    [deleteConfirm, deleteCharge],
  );

  return (
    <div className="flex-1 flex flex-col gap-6">
      <PageHeader
        title={t("charges.list.title")}
        subtitle={t("charges.list.subtitle")}
        action={<CreateChargeForm />}
      />

      <ChargesKpis />

      <DataTable<ChargeListItem>
        searchPlaceholder={t("charges.list.searchPlaceholder")}
        data={queryData?.items}
        nextCursor={queryData?.nextCursor}
        hasMore={queryData?.hasMore}
        columns={columns}
        queryKey={chargesKeys.lists(search.q)}
        navigateFrom="/charges"
        searchFrom="/_private/charges/"
        onDelete={handleDelete}
        headerActions={
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => exportCharges(search.q)}
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("charges.list.actions.exportXlsx")}</TooltipContent>
          </Tooltip>
        }
        searchable={true}
      />

      {deleteConfirm.confirmDialog}
    </div>
  );
}
